/**
 * 出纳付款（延伸需求 3.5）
 *
 * 已批准、待付款的报销单。出纳在这里看到"该付谁多少钱"，付完回系统登记。
 *
 * 【为什么不做成"审批"】付款执行不是审批判断，是执行动作——
 * 它的前置条件明确（单子已批准、未付清），动作也明确（登记一笔付款）。
 * 与审批混在一页会让人分不清"我通过的是节点还是付了钱"。
 *
 * 【对接银行属二期】目前由出纳在网银完成后再回来登记，所以登记即"已付款"。
 * 界面上如实说明这一点，不假装有付款通道。
 */

import { useState } from 'react';
import { Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ExpensePaymentMethod,
  EXPENSE_PAYMENT_METHOD_LABELS,
  ExpenseReimburseStatus,
  ExpenseType,
  EXPENSE_TYPE_LABELS,
  PermissionCode,
} from '@ilc/shared';
import type { ExpenseReimburse } from '@ilc/shared';
import { expenseApi } from '@/api/endpoints';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { Money, moneyCellStyle } from '@/components/Money';
import { MaskedField } from '@/components/MaskedField';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  expenseType?: ExpenseType;
  status?: ExpenseReimburseStatus;
}

export function ExpensePayPage(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canPay = can(PermissionCode.EXPENSE_PAY);

  const list = usePagedList<ExpenseReimburse, Query>({
    loader: (q) => expenseApi.listReimburse(q),
    /**
     * 默认看**已批准**（待付款）。已付清的单子（PAID）不在这里——
     * 出纳的待办列表里混进办完的事，久了就没人看了。
     */
    initialQuery: { page: 1, pageSize: 20, status: ExpenseReimburseStatus.APPROVED },
  });

  const [target, setTarget] = useState<ExpenseReimburse | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const doPay = async (): Promise<void> => {
    if (!target) return;
    const v = await form.validateFields();
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          expenseApi.pay(target.id, {
            amountCents: v.amountYuan ? Math.round(Number(v.amountYuan) * 100) : undefined,
            method: v.method,
            payDate: v.payDate ? dayjs(v.payDate).format('YYYY-MM-DD') : null,
            remark: v.remark || null,
          }),
        successMessage: '付款已登记',
        offline: () => undefined,
        offlineMessage: '后端付款接口尚未就绪，本次登记仅在本地演示',
      });
      if (result.ok) {
        setTarget(null);
        form.resetFields();
        list.reload();
      }
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '报销单号',
      dataIndex: 'reimburseNo',
      width: 170,
      render: (v: string, r: ExpenseReimburse) => (
        <Link to={`/business/expenses/reimburse/${r.id}`} className="ilc-mono">
          {v}
        </Link>
      ),
    },
    { title: '申请人', dataIndex: 'applicantName', width: 100, render: (v: string | null) => v ?? '-' },
    { title: '部门', dataIndex: 'deptName', width: 110, render: (v: string | null) => v ?? '-' },
    {
      title: '费用类型',
      dataIndex: 'expenseType',
      width: 100,
      render: (v: string) => EXPENSE_TYPE_LABELS[v as ExpenseType] ?? v,
    },
    {
      title: '应付金额',
      dataIndex: 'amountCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      render: (v: number) => <Money cents={v} strong />,
    },
    { title: '收款人', dataIndex: 'payeeName', width: 110, render: (v: string | null) => v ?? '-' },
    {
      title: '收款账号',
      dataIndex: 'payeeAccount',
      width: 170,
      // 账号是敏感字段：只有持付款权限的人看得到明文，其余一律脱敏
      render: (v: string | null) => (
        <MaskedField value={v} kind="bankAccount" permission={PermissionCode.EXPENSE_PAY} allowReveal />
      ),
    },
    { title: '开户行', dataIndex: 'payeeBank', width: 160, ellipsis: true, render: (v: string | null) => v ?? '-' },
    { title: '报销事由', dataIndex: 'reason', ellipsis: true },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, r: ExpenseReimburse) => (
        <Button size="small" type="link" disabled={!canPay} onClick={() => setTarget(r)}>
          登记付款
        </Button>
      ),
    },
  ];

  return (
    <div className="ilc-page">
      <PageHeader
        title="出纳付款"
        description="已批准待付款的报销单"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="付款执行目前是「线下付款 + 回来后登记」，对接银行资金系统属二期"
        description={
          <span>
            登记即表示<strong>钱已经付出去</strong>。留空金额表示付清剩余部分；
            支持分次登记（分期付款、公务卡等）。
            <br />
            付款后系统会把关联发票推进到「已入账」语境，但<strong>不会生成记账凭证</strong>——
            凭证必须在 T+ 端由财务人工选科目后生成，凭号回写到这张单上。
          </span>
        }
      />

      {!canPay && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="你没有付款权限，本页只读"
        />
      )}

      <DataTable<ExpenseReimburse>
        storageKey="expense-pay"
        exportFileName="待付款报销单"
        exportColumns={[
          { title: '报销单号', dataIndex: 'reimburseNo' },
          { title: '申请人', dataIndex: 'applicantName' },
          { title: '部门', dataIndex: 'deptName' },
          { title: '费用类型', value: (r) => EXPENSE_TYPE_LABELS[r.expenseType] ?? r.expenseType },
          { title: '应付金额(元)', value: (r) => (r.amountCents / 100).toFixed(2) },
          { title: '收款人', dataIndex: 'payeeName' },
          { title: '开户行', dataIndex: 'payeeBank' },
          { title: '报销事由', dataIndex: 'reason' },
        ]}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        scrollX={1400}
        emptyText="当前没有待付款的报销单"
        footer={() =>
          list.list.length > 0 ? (
            <Space size={16}>
              <strong>本页待付合计：</strong>
              <Money
                cents={list.list.reduce((sum, r) => sum + (r.amountCents ?? 0), 0)}
                strong
              />
            </Space>
          ) : null
        }
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="单号 / 事由"
              style={{ width: 220 }}
              onSearch={(v) => list.setQuery({ keyword: v || undefined })}
            />
            <Select
              allowClear
              placeholder="状态"
              style={{ width: 130 }}
              value={list.query.status}
              onChange={(v) => list.setQuery({ status: v })}
              options={[
                { value: ExpenseReimburseStatus.APPROVED, label: '待付款' },
                { value: ExpenseReimburseStatus.PAID, label: '已付款' },
                { value: undefined, label: '全部' },
              ].filter((o) => o.value !== undefined || true)}
            />
            <Select
              allowClear
              placeholder="费用类型"
              style={{ width: 130 }}
              value={list.query.expenseType}
              onChange={(v) => list.setQuery({ expenseType: v })}
              options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Modal
        open={target !== null}
        title={`登记付款：${target?.reimburseNo ?? ''}`}
        onCancel={() => setTarget(null)}
        onOk={() => void doPay()}
        confirmLoading={saving}
        destroyOnHidden
        okText="确认已付款"
        width={560}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`应付 ¥${((target?.amountCents ?? 0) / 100).toFixed(2)}　收款人：${target?.payeeName ?? '-'}`}
          description="确认后表示钱已付出。登记记录会写入操作日志，付款金额不能超过未付余额。"
        />
        <Form
          key={target?.id ?? 'pay'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{ method: ExpensePaymentMethod.BANK, amountYuan: (target?.amountCents ?? 0) / 100 }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="amountYuan" label="付款金额（元）" extra="默认全额，可改成部分">
                <InputNumber style={{ width: '100%' }} precision={2} min={0.01} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="method" label="付款方式" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(EXPENSE_PAYMENT_METHOD_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="payDate" label="付款日期">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注" extra="如流水号、回单编号">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
        <Tag color="default">
          付款完成后，记账凭证仍需在 T+ 端人工选科目生成——系统不会自动生成凭证。
        </Tag>
      </Modal>
    </div>
  );
}

export default ExpensePayPage;
